import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { ClipboardEdit, LineChart, Grid3x3, BarChart3, Coins, TrendingUp } from "lucide-react";
import DailyEntry from "@/components/ops/DailyEntry";
import PivotBuilder from "@/components/ops/PivotBuilder";
import PnLDiagnostic from "@/components/ops/PnLDiagnostic";
import ItemProfit from "@/components/ops/ItemProfit";
import ROICalculator from "@/components/ops/ROICalculator";

/**
 * Outlet Command Centre shell.
 *   • Header  — hero + master-data KPIs + next-phase pointer
 *   • Tabs    — Daily entry · P&L Diagnostic · Pivot · Master data
 */
export default function CommandCentreTab() {
  const [status, setStatus] = useState(null);
  const [outlets, setOutlets] = useState([]);
  const [channels, setChannels] = useState([]);
  const [cats, setCats] = useState([]);
  const [inner, setInner] = useState("entry");

  const load = () => Promise.all([
    api.get("/ops/status").then(r => setStatus(r.data)),
    api.get("/ops/outlets").then(r => setOutlets(r.data)),
    api.get("/ops/channels").then(r => setChannels(r.data)),
    api.get("/ops/expense-cats").then(r => setCats(r.data)),
  ]);
  useEffect(() => { load(); }, []);

  return (
    <div>
      {/* Hero */}
      <div className="rounded-2xl p-6 md:p-8 border mb-6" style={{ background: "#FFFDF6", borderColor: "#E4D6A8" }}>
        <div className="font-mono2 text-[10px] tracking-[0.4em] uppercase mb-2" style={{ color: "#B4841F" }}>Outlet Command Centre</div>
        <h2 className="font-display text-3xl md:text-4xl" style={{ color: "#2b1a0d" }}>Diagnostic &amp; ROI Engine</h2>
        <p className="mt-2 text-sm md:text-base max-w-3xl" style={{ color: "#5b4530" }}>
          Analysis-first system for root-cause diagnosis across every outlet, channel, category, item and expense. Not a billing tool.
        </p>
        {status && (
          <div className="mt-5 grid grid-cols-2 md:grid-cols-5 gap-3">
            {[
              ["Outlets seeded", status.outlets],
              ["Sales channels", status.channels],
              ["Expense cats", status.expense_cats],
              ["Fixed / Variable", `${status.fixed_cats} / ${status.variable_cats}`],
              ["Months locked", status.month_locks],
            ].map(([k, v]) => (
              <div key={k} className="rounded-xl p-3 border" style={{ background: "#FFF8E7", borderColor: "#E4D6A8" }}>
                <div className="text-[10px] font-mono2 uppercase tracking-widest" style={{ color: "#7a6349" }}>{k}</div>
                <div className="font-display text-2xl" style={{ color: "#2b1a0d" }}>{v}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      <Tabs value={inner} onValueChange={setInner}>
        <div className="overflow-x-auto -mx-4 px-4 pb-1 mb-4">
          <TabsList className="bg-white border border-[#E4D6A8] rounded-full p-1 flex-nowrap w-max">
            <TabsTrigger value="entry" data-testid="cc-tab-entry" className="rounded-full data-[state=active]:bg-[#F5C242] data-[state=active]:text-black">
              <ClipboardEdit size={14} className="mr-2" /> Daily entry
            </TabsTrigger>
            <TabsTrigger value="pnl" data-testid="cc-tab-pnl" className="rounded-full data-[state=active]:bg-[#F5C242] data-[state=active]:text-black">
              <LineChart size={14} className="mr-2" /> P&amp;L diagnostic
            </TabsTrigger>
            <TabsTrigger value="item" data-testid="cc-tab-item" className="rounded-full data-[state=active]:bg-[#F5C242] data-[state=active]:text-black">
              <Coins size={14} className="mr-2" /> Item profit
            </TabsTrigger>
            <TabsTrigger value="pivot" data-testid="cc-tab-pivot" className="rounded-full data-[state=active]:bg-[#F5C242] data-[state=active]:text-black">
              <Grid3x3 size={14} className="mr-2" /> Pivot
            </TabsTrigger>
            <TabsTrigger value="master" data-testid="cc-tab-master" className="rounded-full data-[state=active]:bg-[#F5C242] data-[state=active]:text-black">
              <BarChart3 size={14} className="mr-2" /> Master data
            </TabsTrigger>
            <TabsTrigger value="roi" data-testid="cc-tab-roi" className="rounded-full data-[state=active]:bg-[#F5C242] data-[state=active]:text-black">
              <TrendingUp size={14} className="mr-2" /> ROI calculator
            </TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="entry">
          <DailyEntry />
        </TabsContent>

        <TabsContent value="pnl">
          <PnLDiagnostic />
        </TabsContent>

        <TabsContent value="item">
          <ItemProfit />
        </TabsContent>

        <TabsContent value="pivot">
          <PivotBuilder />
        </TabsContent>

        <TabsContent value="master">
          <MasterData outlets={outlets} channels={channels} cats={cats} onDone={load} />
        </TabsContent>

        <TabsContent value="roi">
          <ROICalculator />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// -----------------------------------------------------------------------------
function MasterData({ outlets, channels, cats, onDone }) {
  const [newCh, setNewCh] = useState({ name: "", channel_type: "aggregator" });
  const [newCat, setNewCat] = useState({ name: "", kind: "Variable" });

  const addChannel = async () => {
    const name = (newCh.name || "").trim();
    if (!name) return toast.error("Channel name required");
    try { await api.post("/ops/channels", { name, channel_type: newCh.channel_type });
      toast.success("Channel added"); setNewCh({ name: "", channel_type: "aggregator" }); onDone(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const addCat = async () => {
    const name = (newCat.name || "").trim();
    if (!name) return toast.error("Category name required");
    try { await api.post("/ops/expense-cats", { name, kind: newCat.kind });
      toast.success("Category added"); setNewCat({ name: "", kind: "Variable" }); onDone(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const groupedChannels = { aggregator: [], payment: [], other: [] };
  channels.forEach(c => { (groupedChannels[c.channel_type] || groupedChannels.other).push(c); });

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
      {/* Outlets */}
      <div className="rounded-2xl border bg-white p-5 lg:col-span-2" style={{ borderColor: "#E4D6A8" }}>
        <h3 className="font-display text-xl mb-3" style={{ color: "#2b1a0d" }}>Outlets ({outlets.length})</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 max-h-72 overflow-y-auto">
          {outlets.map(o => (
            <div key={o.id} className="flex items-center justify-between text-sm py-1.5 px-3 border rounded-lg" style={{ borderColor: "#F3E7C4", color: "#2b1a0d", background:"#FFF8E7" }} data-testid={`ops-outlet-${o.outlet_code || o.id}`}>
              <span className="truncate mr-2">{o.name}</span>
              {o.outlet_code && <span className="font-mono2 text-[10px] px-2 py-0.5 rounded-full" style={{ background: "#F5EFE0", color: "#7a6349" }}>ID {o.outlet_code}</span>}
            </div>
          ))}
        </div>
      </div>

      {/* Channels */}
      <div className="rounded-2xl border bg-white p-5" style={{ borderColor: "#E4D6A8" }}>
        <h3 className="font-display text-xl mb-3" style={{ color: "#2b1a0d" }}>Sales channels ({channels.length})</h3>
        {["aggregator","payment","other"].map(t => (
          <div key={t} className="mb-3">
            <div className="text-[10px] font-mono2 uppercase tracking-widest mb-1" style={{color:"#7a6349"}}>{t}</div>
            <div className="flex flex-wrap gap-2">
              {groupedChannels[t].map(c => (
                <span key={c.id} className="text-xs px-3 py-1 rounded-full border" style={{ background: "#FFF8E7", borderColor: "#E4D6A8", color: "#3B2519" }}>{c.name}</span>
              ))}
              {groupedChannels[t].length === 0 && <span className="text-xs" style={{color:"#b0967a"}}>—</span>}
            </div>
          </div>
        ))}
        <div className="mt-3 flex items-center gap-2 pt-3 border-t" style={{ borderColor: "#F3E7C4" }}>
          <input value={newCh.name} onChange={e => setNewCh({...newCh, name: e.target.value})} placeholder="Channel name"
            className="h-8 px-2 rounded border bg-white text-sm flex-1" style={{borderColor:"#E4D6A8"}}
            data-testid="master-ch-name" />
          <select value={newCh.channel_type} onChange={e => setNewCh({...newCh, channel_type: e.target.value})} className="h-8 px-2 rounded border bg-white text-sm" style={{borderColor:"#E4D6A8"}} data-testid="master-ch-type">
            <option value="aggregator">Aggregator</option>
            <option value="payment">Payment</option>
            <option value="other">Other</option>
          </select>
          <Button size="sm" data-testid="master-add-ch" onClick={addChannel}
            className="h-8 rounded-full bg-[#3B2519] hover:bg-[#5a3826] text-[#F5C242] text-xs">+ Add</Button>
        </div>
      </div>

      {/* Expense categories */}
      <div className="rounded-2xl border bg-white p-5" style={{ borderColor: "#E4D6A8" }}>
        <h3 className="font-display text-xl mb-3" style={{ color: "#2b1a0d" }}>Expense categories ({cats.length})</h3>
        {["Variable","Fixed"].map(k => (
          <div key={k} className="mb-3">
            <div className="text-[10px] font-mono2 uppercase tracking-widest mb-1" style={{color:"#7a6349"}}>{k}</div>
            <div className="flex flex-wrap gap-2">
              {cats.filter(c => c.kind === k).map(c => (
                <span key={c.id} className={`text-xs px-3 py-1 rounded-full border ${k === "Fixed" ? "bg-blue-50 text-blue-800" : "bg-amber-50 text-amber-800"}`} style={{ borderColor: "#E4D6A8" }}>{c.name}</span>
              ))}
              {cats.filter(c => c.kind === k).length === 0 && <span className="text-xs" style={{color:"#b0967a"}}>—</span>}
            </div>
          </div>
        ))}
        <div className="mt-3 flex items-center gap-2 pt-3 border-t" style={{ borderColor: "#F3E7C4" }}>
          <input value={newCat.name} onChange={e => setNewCat({...newCat, name: e.target.value})} placeholder="Category name"
            className="h-8 px-2 rounded border bg-white text-sm flex-1" style={{borderColor:"#E4D6A8"}}
            data-testid="master-cat-name" />
          <select value={newCat.kind} onChange={e => setNewCat({...newCat, kind: e.target.value})} className="h-8 px-2 rounded border bg-white text-sm" style={{borderColor:"#E4D6A8"}} data-testid="master-cat-kind">
            <option value="Variable">Variable</option>
            <option value="Fixed">Fixed</option>
          </select>
          <Button size="sm" data-testid="master-add-cat" onClick={addCat}
            className="h-8 rounded-full bg-[#3B2519] hover:bg-[#5a3826] text-[#F5C242] text-xs">+ Add</Button>
        </div>
      </div>
    </div>
  );
}
